// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import * as Log from "@opencode-ai/core/util/log"
import { removeFileQuiet } from "@opencode-ai/core/util/fs"
import type { UserInfo } from "./types"
import { authErrorDetails, recordLoginFailure } from "./diagnostics"
import { HostData } from "../host-data"
import { Flock } from "@opencode-ai/core/util/flock"

const LOG_PREFIX = "[infcode:auth-storage]"
const log = Log.create({ service: LOG_PREFIX })

/**
 * InfCode 认证持久化层。
 *
 * auth.json 是 CLI、VS Code 扩展和 Acode provider 共同消费的本地认证文件。这个模块
 * 负责默认值、缓存、文件权限和 undefined 清理；调用方不应该自己拼路径或直接读写
 * token 文件，否则很容易绕过缓存或把凭证写成过宽权限。
 *
 * 锁约定：只有 save() 获取跨进程 "write" 锁；load() 依赖 tmp+rename 原子写，读路径不加锁。
 * "write" 临界区内禁止再 acquire 任何锁（Flock 不可重入）。refresh 锁只存在于 InfcodeClient.ensure。
 */
export namespace AuthStorage {
  /**
   * 官方服务默认地址。
   *
   * 公网登录会强制回到这个地址，私有化登录才允许使用用户输入或已保存的 baseUrl。
   */
  export const base = "https://www.tokensinfinity.com"

  /**
   * 官方服务默认应用 clientId。
   *
   * 这个值写入 auth.json，供依赖本地配置的 provider 使用；浏览器登录 URL 里的
   * client_id 由 AuthUrl 按登录站点协议单独生成。
   */
  export const client = "928e0f2cb7a7635da60c"

  /**
   * auth.json 归一化后的结构。
   *
   * baseUrl/clientId 始终有默认值；token 和 userInfo 只有登录成功后才存在。
   */
  export type Info = {
    baseUrl: string
    clientId: string
    deepmapUrl?: string
    langfuseEnabled?: boolean
    accessToken?: string
    refreshToken?: string
    userInfo?: UserInfo
  }
  export type TInfo = Info
  export type Options = {
    dir?: string
    signal?: AbortSignal
    expected?: Pick<Info, "accessToken" | "refreshToken" | "baseUrl" | "clientId">
    timeoutMs?: number
  }

  // Crash leftovers become stealable well before a refresh/network budget expires.
  const lockStaleMs = 5_000
  const lockTimeoutMs = 5_000
  // In-flight tmp+rename writes are newer than this; load() must not delete them.
  const leftoverTempMaxAgeMs = 60_000

  export class Changed extends Error {}

  // cache 只绑定当前 auth.json 路径；测试或自定义配置切换路径后会重新读取。
  let corrupted = false
  export function isCorrupted() {
    return corrupted
  }

  let cache: Info | undefined
  let cached: string | undefined
  // CLI login/logout 与 IDE 后端是不同进程，共享同一份 auth.json。缓存命中前比对
  // mtime+size，避免外部进程改写后本进程继续用启动时的旧 token。
  let cachedStat: { mtimeMs: number; size: number } | undefined
  let saves: Promise<void> = Promise.resolve()

  async function waitForWrite(pending: Promise<void>, signal?: AbortSignal) {
    signal?.throwIfAborted()
    if (signal) {
      let abort!: () => void
      await new Promise<void>((resolve, reject) => {
        abort = () => reject(signal.reason)
        signal.addEventListener("abort", abort, { once: true })
        pending.then(resolve, reject)
      }).finally(() => signal.removeEventListener("abort", abort))
      signal.throwIfAborted()
    } else await pending
  }

  async function removeLeftoverTempFiles(dir: string) {
    const names = await fs.readdir(dir).catch(() => [] as string[])
    const cutoff = Date.now() - leftoverTempMaxAgeMs
    await Promise.all(
      names
        .filter((name) => name.startsWith("auth.json.") && name.endsWith(".tmp"))
        .map(async (name) => {
          const file = path.join(dir, name)
          try {
            const stat = await fs.stat(file)
            if (stat.mtimeMs > cutoff) return
          } catch {
            return
          }
          await removeFileQuiet(file, {
            log,
            message: "remove leftover auth temp file failed",
            extra: { name },
          })
        }),
    )
  }

  async function fileStat(file: string) {
    try {
      const stat = await fs.stat(file)
      return { mtimeMs: stat.mtimeMs, size: stat.size }
    } catch (error) {
      if (error && typeof error === "object" && "code" in error && error.code === "ENOENT") return undefined
      throw error
    }
  }

  function sameStat(
    left: { mtimeMs: number; size: number } | undefined,
    right: { mtimeMs: number; size: number } | undefined,
  ) {
    if (!left && !right) return true
    if (!left || !right) return false
    return left.mtimeMs === right.mtimeMs && left.size === right.size
  }

  /**
   * 读取认证配置。
   *
   * 返回值总是经过 info() 归一化，所以调用方可以直接使用 baseUrl/clientId；文件不存在
   * 时也不会创建文件，只有 save() 才会落盘。
   */
  export async function load(input: Options = {}) {
    await waitForWrite(saves, input.signal)
    const file = filepath(input)
    const stat = await fileStat(file)
    // auth.json 是热路径，路径且 mtime/size 都未变时避免每次状态查询都重新读盘。
    if (cache && cached === file && sameStat(cachedStat, stat)) return cache
    await removeLeftoverTempFiles(path.dirname(file))
    if (!stat) {
      corrupted = false
      cache = {
        baseUrl: base,
        clientId: client,
      }
      cached = file
      cachedStat = stat
      return cache
    }
    cache = info((await read(file)) ?? {})
    cached = file
    cachedStat = stat
    return cache
  }

  // In-memory view without restatting the file. reloadIfChanged uses this as "before"
  // so a cross-process write is visible as a diff against this process's last known auth.
  export function peek(input: Options = {}) {
    const file = filepath(input)
    if (cache && cached === file) return cache
    return undefined
  }

  function writeLock(opts: Options) {
    return {
      dir: path.join(dir(opts), ".auth-locks"),
      signal: opts.signal,
      staleMs: lockStaleMs,
      timeoutMs: opts.timeoutMs ?? lockTimeoutMs,
    }
  }

  // Merge the patch under the shared write lock; undefined fields remove saved credentials.
  export async function save(input: Partial<Info>, opts: Options = {}) {
    const commit = () => Flock.withLock("write", () => write(input, opts), writeLock(opts))
    const next = saves.then(commit, commit)
    saves = next.catch(() => undefined)
    await waitForWrite(next, opts.signal)
  }

  async function write(input: Partial<Info>, opts: Options) {
    const file = filepath(opts)
    const auth = (await read(file)) ?? {}
    const current = info(auth)
    if (
      opts.expected &&
      (["accessToken", "refreshToken", "baseUrl", "clientId"] as const).some(
        (key) => current[key] !== opts.expected![key],
      )
    ) {
      throw new Changed("InfCode login changed during refresh")
    }
    const next = clean({
      ...auth,
      ...input,
    })
    // 令牌文件按最小权限写入，避免本机其他用户直接读取凭证。
    // 先写临时文件再 rename，读路径才能免锁且不会读到半截 JSON。
    await fs.mkdir(path.dirname(file), { recursive: true, mode: 0o700 })
    const tmp = `${file}.${crypto.randomUUID()}.tmp`
    await Bun.write(tmp, `${JSON.stringify(next, null, 2)}\n`)
    await fs.chmod(tmp, 0o600).catch(async (err: unknown) => {
      log.warn("failed to restrict auth temp file permissions", { tmp, err })
      await removeFileQuiet(tmp, { log, message: "remove temp auth file failed", extra: { tmp } })
      throw err
    })
    await fs.rename(tmp, file).catch(async (err: unknown) => {
      await removeFileQuiet(tmp, { log, message: "remove temp auth file failed", extra: { tmp } })
      throw err
    })
    cache = info(next)
    corrupted = false
    cached = file
    cachedStat = await fileStat(file)
  }

  /**
   * 清空内存缓存。
   *
   * 主要给测试或配置路径切换使用；不会删除 auth.json，也不会改变磁盘登录态。
   */
  export function reset() {
    cache = undefined
    cached = undefined
    cachedStat = undefined
    corrupted = false
  }

  /**
   * 丢弃内存缓存并重新从磁盘读取 auth.json。
   *
   * load() 已会按 mtime/size 感知跨进程写入；reload() 仍提供显式失效入口，
   * 供 HTTP `?reload=true` 等调用方在不依赖 stat 的情况下强制再读磁盘。
   */
  export async function reload(input: Options = {}) {
    reset()
    return load(input)
  }

  /**
   * 统一解析认证目录。
   *
   * 只有 auth.json 需要按目录隔离；默认目录是 InfCode 的本地认证目录。
   */
  export function dir(input: Options = {}) {
    if (input.dir) return input.dir
    return HostData.authRoot()
  }

  /**
   * 统一解析 auth.json 路径。
   */
  function filepath(input: Options = {}) {
    return path.join(dir(input), "auth.json")
  }

  /**
   * 读取原始 JSON 文件。
   *
   * 这里保持最小解析，不做默认值填充；默认值统一在 info() 中处理。
   */
  async function read(file: string) {
    if (!(await Bun.file(file).exists())) return undefined
    const text = await Bun.file(file).text()
    try {
      const parsed = JSON.parse(text)
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        corrupted = true
        return undefined
      }
      corrupted = false
      return parsed as Partial<Info>
    } catch (err) {
      if (err instanceof SyntaxError) {
        corrupted = true
        // Base64 可还原，解析异常 message 也可能含凭证；只保留文件元信息。
        log.warn("invalid auth json, falling back to defaults", {
          path: file,
          bytes: text.length,
          ...authErrorDetails(err),
        })
        return undefined
      }
      throw err
    }
  }

  /**
   * 删除 undefined 字段。
   *
   * logout 通过传 undefined 清 token，clean() 让最终文件真正移除这些 key。
   */
  function clean(value: Partial<Info>) {
    return Object.fromEntries(Object.entries(value).filter((entry) => entry[1] !== undefined)) as Partial<Info>
  }

  /**
   * 将部分配置归一化为完整 Info。
   *
   * 对外只暴露这一种稳定形态，避免每个调用点都重复处理默认 baseUrl/clientId。
   */
  function info(value: Partial<Info>): Info {
    return {
      baseUrl: value.baseUrl ?? base,
      clientId: value.clientId ?? client,
      ...(value.deepmapUrl ? { deepmapUrl: value.deepmapUrl } : {}),
      langfuseEnabled: value.langfuseEnabled,
      accessToken: value.accessToken,
      refreshToken: value.refreshToken,
      userInfo: value.userInfo,
    }
  }
}
